import { useRef, useCallback, useState } from 'react'
import type { Store } from '@/types'
import { StoreCard } from './StoreCard'
import { StoreCardSkeleton } from './ui/StoreCardSkeleton'
import type { DeliveryAddressParams } from '@/utils/storeRedirect'

interface StoreListProps {
  stores: Store[]
  variant: 'pickup' | 'delivery'
  onSelect?: (store: Store) => void
  loading?: boolean
  showDetails?: boolean
  deliveryAddress?: DeliveryAddressParams | null
}

export function StoreList({
  stores,
  variant,
  onSelect,
  loading = false,
  showDetails = true,
  deliveryAddress,
}: StoreListProps) {
  const listRef = useRef<HTMLDivElement>(null)
  const [pullDistance] = useState(0)
  const [isRefreshing] = useState(false)

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent, index: number) => {
    const cards = listRef.current?.querySelectorAll('[data-store-card]')
    if (!cards) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        const nextCard = cards[index + 1] as HTMLElement
        nextCard?.focus()
        break
      case 'ArrowUp':
        e.preventDefault()
        const prevCard = cards[index - 1] as HTMLElement
        prevCard?.focus()
        break
    }
  }, [])

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <StoreCardSkeleton key={i} variant={variant} index={i} />
        ))}
      </div>
    )
  }

  if (stores.length === 0) {
    return null
  }

  return (
    <div 
      ref={listRef}
      className="relative"
      role="list"
      aria-label={`${variant === 'pickup' ? 'Pickup' : 'Delivery'} stores`}
    >
      {/* Pull to refresh indicator */}
      {(pullDistance > 0 || isRefreshing) && (
        <div 
          className="absolute left-1/2 -translate-x-1/2 -top-2 flex items-center justify-center transition-transform"
          style={{ transform: `translateX(-50%) translateY(${pullDistance}px)` }}
        >
          <div className={`size-6 border-2 border-brand-primary border-t-transparent rounded-full ${isRefreshing ? 'animate-spin' : ''}`} />
        </div>
      )}

      {/* Screen reader announcements */}
      <div className="sr-only" role="status" aria-live="polite">
        {stores.length} stores found
      </div>

      <div 
        className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-12"
        style={{ transform: `translateY(${pullDistance * 0.3}px)` }}
      >
        {stores.map((store, index) => (
          <div 
            key={store.storeId || store.id}
            className="animate-fade-in-up"
            style={{ animationDelay: `${index * 50}ms` }}
            role="listitem"
            data-store-card
            tabIndex={0}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            <StoreCard
              store={store}
              variant={variant}
              onSelect={onSelect}
              showDetails={showDetails}
              preloadImage={index < 4}
              deliveryAddress={deliveryAddress}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
